import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../../ui/card";
import InputField from "./InputField";
import { useReactFlow } from "@xyflow/react";
import CheckboxInputField from "./CheckboxInputField";
import { cn } from "../../../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Switch } from "../../ui/switch";
import { Label } from "../../ui/label";
import { BlocksType, NodeData } from "./types";
import DropDownField from "./DropDownField";
import { useForex } from "@/components/blocks/flow/StrategyViewModal";

interface Props {
  data: NodeData;
  id: string;
  disabled?: boolean;
}

interface InputHintsType {
  [indicator: string]: {
    [field: string]: string;
  };
}

const InputHints: InputHintsType = {
  "Volume Weighted Average Price": {
    period:
      "Period - Default VWAP is unsmoothed. Adjust the period for smoothing.",
    lookback:
      "Lookback - Calculated from the start of the trading session. Lookback is per session not per bar.",
  },
};

const MAX_PERCENTAGE = 1;

const customMessege = {
  Indicator:
    "We have set your indicator period settings to 2 and 10 in this example.",
  Action:
    "We have set your Take Profit to 0.5% and your Stop Loss to 0.5% which is a risk/reward ratio of 1:1.",
  Data: "Use the drag-and-drop connectors to connect the inputs and outputs of all blocks (The blue connectors)",
  BuyWarning:
    "Higher TP or SL values (percentages) can result in fewer trades in backtests or forward tests, especially when price volatility is low, because the market does not move enough pips to trigger entries or exits frequently.",
};

function updateValueByIndex(config: any, index: number, newValue: any) {
  if (index < 0 || index >= config.length) {
    console.error("Index out of bounds");
    return config;
  }
  return config.map((item: any, i: number) =>
    i === index ? { ...item, value: newValue } : item,
  );
}

const InputCard: React.FC<Props> = ({ data, id, disabled = false }) => {
  const { updateNodeData } = useReactFlow();
  const { isForex } = useForex();

  const [warningInputs, setWarningInputs] = useState<string[]>([]);
  const isBuyOrSellBlock =
    data.type === BlocksType.ACTION &&
    ["Buy", "Sell", , "Buy (Long)", "Sell (Short)"].includes(data.name);
  const getInitialToggleState = (): boolean => {
    if (!isBuyOrSellBlock) return true;
    const hasPercentageValues = data.other_inputs?.some((item) => {
      const isPercentageField =
        item.name === "Take Profit %" ||
        item.name === "Stop Loss %" ||
        item.name === "Trailing SL%";
      return (
        isPercentageField &&
        (item.value !== null || item.checked_state === true)
      );
    });

    const hasPriceOrPipsValues = data.other_inputs?.some((item) => {
      const isPipsOrPrice = isForex
        ? item.name === "Take Profit Pips" || item.name === "Stop Loss Pips"
        : item.name === "Take Profit Price" || item.name === "Stop Loss Price";
      return isPipsOrPrice && item.value !== null;
    });

    if (hasPercentageValues) return true;
    if (hasPriceOrPipsValues) return false;

    return true; // default fallback
  };

  const [usePercentage, setUsePercentage] = useState(getInitialToggleState);
  const onChange = (index: number, newValue: any) => {
    const updates =
      typeof newValue === "object" ? newValue : { value: newValue };
    updateNodeData(id, {
      ...data,
      other_inputs: data.other_inputs?.map((item: any, i: number) =>
        i === index ? { ...item, ...updates } : item,
      ),
    });
  };

  const handleToggle = (checked: boolean) => {
    setUsePercentage(checked);

    const updatedInputs = data.other_inputs?.map((item) => {
      if (!isBuyOrSellBlock) return item;

      const name = item?.name;
      const isPercentageField =
        name === "Take Profit %" ||
        name === "Stop Loss %" ||
        name === "Trailing SL%";
      const isPipsOrPriceField = isForex
        ? name === "Take Profit Pips" || name === "Stop Loss Pips"
        : name === "Take Profit Price" || name === "Stop Loss Price";

      if (checked && isPipsOrPriceField) {
        return { ...item, value: null, checked_state: false };
      }
      if (!checked && isPipsOrPriceField) {
        return { ...item, value: item?.min_value };
      }
      if (!checked && isPercentageField) {
        return { ...item, value: null };
      }
      if (checked && isPercentageField) {
        return { ...item, value: item?.min_value, checked_state: true };
      }

      return item;
    });

    updateNodeData(id, { ...data, other_inputs: updatedInputs });
  };

  useEffect(() => {
    if (!usePercentage || !isBuyOrSellBlock || !isForex) {
      setWarningInputs([]);
      return;
    }

    const warnings =
      data?.other_inputs
        ?.filter(
          (item) =>
            (item.name === "Take Profit %" || item.name === "Stop Loss %") &&
            item.value > MAX_PERCENTAGE,
        )
        .map((item) => item.name) || [];

    setWarningInputs(warnings);
  }, [usePercentage, isBuyOrSellBlock, data?.other_inputs]);
  isBuyOrSellBlock && console.log(data?.other_inputs);
  return (
    <Popover open={false}>
      <PopoverTrigger asChild>
        {!data?.other_inputs || data?.other_inputs?.length === 0 ? (
          <div className="p-1"></div>
        ) : (
          <Card className="bg-transparent border-none shadow-none ">
            {isBuyOrSellBlock && (
              <div className="flex text-[0.6rem] text-muted-foreground items-center justify-between  p-4 gap-4 w-full">
                <p className="font-bold">{"Set Take Profit and Stop Loss"}</p>
                <div className="flex gap-2  h-4">
                  <Label className="text-[0.5rem]">
                    {isForex ? "Pips" : "Price"}
                  </Label>
                  <Switch
                    className="h-4"
                    checked={usePercentage}
                    onCheckedChange={handleToggle}
                    disabled={disabled}
                  />
                  <Label className="text-xs">%</Label>
                </div>
              </div>
            )}
            <CardContent
              className={cn("p-0 grid grid-cols-2 gap-x-3 gap-y-4 ")}
            >
              {data?.other_inputs?.map((item, index) => {
                const showField = () => {
                  if (data.type !== BlocksType.ACTION) return true;

                  const name = item?.name;

                  if (!isBuyOrSellBlock) return true;

                  if (usePercentage) {
                    return (
                      name === "Take Profit %" ||
                      name === "Stop Loss %" ||
                      name === "Trailing SL%"
                    );
                  } else {
                    return isForex
                      ? name === "Take Profit Pips" || name === "Stop Loss Pips"
                      : name === "Take Profit Price" ||
                          name === "Stop Loss Price";
                  }
                };
                if (!showField()) return null;

                if (item?.type?.toLowerCase() === "input") {
                  return (
                    <InputField
                      key={index}
                      item={item}
                      index={index}
                      onChange={onChange}
                      hint={InputHints?.[data?.name]?.[item?.name]}
                      warning={warningInputs.includes(item?.name)}
                      warningMessege={customMessege["BuyWarning"]}
                      disabled={disabled}
                    />
                  );
                } else if (item?.type?.toLowerCase() === "dropdown") {
                  return (
                    <DropDownField
                      key={index}
                      item={item}
                      index={index}
                      onChange={onChange}
                      disabled={disabled}
                    />
                  );
                } else if (item?.type?.toLowerCase() === "checkbox input") {
                  return (
                    <CheckboxInputField
                      key={index}
                      item={item}
                      disabled
                      index={index}
                      onChange={onChange}
                      hint={InputHints?.[data?.name]?.[item?.name]}
                      warning={warningInputs.includes(item?.name)}
                      warningMessege={customMessege["BuyWarning"]}
                    />
                  );
                }

                return null;
              })}
            </CardContent>
          </Card>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="center"
        side="left"
        className="text-xs w-40"
        asChild
      >
        <div></div>
      </PopoverContent>
    </Popover>
  );
};

export default InputCard;
